import * as i0 from '@angular/core';
import { InjectionToken, inject, Injectable, Optional, Inject, Component, ChangeDetectionStrategy, Input, HostBinding, ENVIRONMENT_INITIALIZER, NgModule } from '@angular/core';
import { of, tap, catchError, finalize, share, take } from 'rxjs';
import { HttpClient } from '@angular/common/http';

const M_ICONS = new InjectionToken('M_ICONS');
const M_ICONS_REGISTRY_PATH = new InjectionToken('M_ICONS_REGISTRY_PATH');

class MIconFolderService {
    constructor() {
        /** Cache for icons loaded by register icon. */
        this.localRegistry = new Map();
        /** Cache for icons loaded by direct URLs. */
        this.registry = new Map();
        /** In-progress icon fetches. Used to coalesce multiple requests to the same URL. */
        this._inProgressUrlFetches = new Map();
        this._iconsBucketUrl = inject(M_ICONS_REGISTRY_PATH, { optional: true });
        this._httpClient = inject(HttpClient, { optional: true });
    }
    /**
     * Loads the icon on the application memory registry.
     *
     * @param {MIconInterface[]} icons List of icons to register
     */
    registerIcon(...icons) {
        icons.forEach((icon) => !this.localRegistry.has(icon.name) && this.localRegistry.set(icon.name, icon.data));
    }
    /**
     * Get the icon instance object
     *
     * @param {MIconType} name Name of the icon
     * @returns {MIconInterface | undefined} The icon registered if exists
     */
    getIconByName(name) {
        const iconName = this.localRegistry.get(name) || this.registry.get(name);
        if (iconName === undefined) {
            // eslint-disable-next-line no-console
            console.warn(`Icon with name "${name}" not found, did you add it to the icon registry?`);
        }
        return iconName ? { name, data: iconName } : undefined;
    }
    /**
     * Get the icon SVG value
     *
     * @param {MIconType} name Name of the icon
     * @returns {Observable<string | undefined>} The icon registered if exists
     */
    getIconByName$(name) {
        const iconName = this.localRegistry.get(name) || this.registry.get(name);
        if (iconName) {
            return of(iconName);
        }
        return this._fetchIcon(name).pipe(tap((svg) => this.registry.set(name, svg)));
    }
    /**
     * Returns an Observable which produces the string contents of the given icon. Results may be
     * cached, so future calls with the same URL may not cause another HTTP request.
     *
     * @param {MIconType} name Name of the icon
     * @returns {Observable<string | undefined>} The icon registered if exists
     */
    _fetchIcon(name) {
        // Store in-progress fetches to avoid sending a duplicate request for a URL when there is
        // already a request in progress for that URL. It's necessary to call share() on the
        // Observable returned by http.get() so that multiple subscribers don't cause multiple XHRs.
        const inProgressFetch = this._inProgressUrlFetches.get(name);
        if (inProgressFetch) {
            return inProgressFetch;
        }
        if (!this._httpClient) {
            throw Error('Could not find HttpClient provider.' +
                'Please include the HttpClientModule from @angular/common/http in your app.module.ts imports.');
        }
        const fetchIconRequest = this._httpClient.get(`${this._iconsBucketUrl}${name}.svg`, { responseType: 'text' }).pipe(catchError(() => {
            // eslint-disable-next-line no-console
            console.warn(`[MIconService]: Icon with name "${name}" not found.`);
            return of(undefined);
        }), finalize(() => this._inProgressUrlFetches.delete(name)), share());
        this._inProgressUrlFetches.set(name, fetchIconRequest);
        return fetchIconRequest;
    }
}
MIconFolderService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MIconFolderService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
MIconFolderService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MIconFolderService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MIconFolderService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

/**
 * Global service to register the icons used in the app
 */
class MIconService extends MIconFolderService {
    constructor(icons) {
        super();
        if (icons) {
            this.registerIcon(...icons);
        }
    }
    /**
     * Get the icon SVG value
     *
     * @param {MIconType} name Name of the icon
     * @returns {Observable<string | undefined>} The icon registered if exists
     */
    getIconByName$(name) {
        return of(this.getIconByName(name)?.data);
    }
}
MIconService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MIconService, deps: [{ token: M_ICONS, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
MIconService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MIconService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MIconService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [M_ICONS]
                }] }]; } });

class MIconComponent {
    constructor(element, renderer) {
        this.element = element;
        this.renderer = renderer;
        this.iconService = inject(MIconService);
    }
    get iconName() {
        return this._iconName;
    }
    set iconName(iconName) {
        if (this._iconName !== iconName) {
            if (this.svgIcon) {
                this.renderer.removeChild(this.element.nativeElement, this.svgIcon);
                delete this.svgIcon;
            }
            this.iconService
                .getIconByName$(iconName)
                .pipe(take(1))
                .subscribe((icon) => {
                if (icon) {
                    this.svgIcon = this.svgElementFromString(icon);
                    this.renderer.appendChild(this.element.nativeElement, this.svgIcon);
                }
            });
            this._iconName = iconName;
        }
    }
    svgElementFromString(svgContent) {
        const div = this.renderer.createElement('div');
        div.innerHTML = svgContent;
        return div.querySelector('svg');
    }
}
MIconComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MIconComponent, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component });
MIconComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.9", type: MIconComponent, selector: "m-icon", inputs: { iconName: "iconName" }, host: { properties: { "attr.aria-label": "this.iconName" } }, ngImport: i0, template: '', isInline: true, styles: [":host{display:inline-block;width:1em;height:1em;vertical-align:middle}:host ::ng-deep svg{fill:currentColor;min-width:1em;vertical-align:top;pointer-events:none}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MIconComponent, decorators: [{
            type: Component,
            args: [{ selector: 'm-icon', template: '', changeDetection: ChangeDetectionStrategy.OnPush, styles: [":host{display:inline-block;width:1em;height:1em;vertical-align:middle}:host ::ng-deep svg{fill:currentColor;min-width:1em;vertical-align:top;pointer-events:none}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }]; }, propDecorators: { iconName: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.aria-label']
            }] } });

class MIconModule {
    static forRoot(icons) {
        if (Array.isArray(icons)) {
            return {
                ngModule: MIconModule,
                providers: [
                    {
                        provide: M_ICONS,
                        useValue: icons
                    }
                ]
            };
        }
        return MIconModule.moduleIconsRegistry(icons);
    }
    static forChild(icons) {
        if (Array.isArray(icons)) {
            return {
                ngModule: MIconModule,
                providers: [
                    {
                        provide: ENVIRONMENT_INITIALIZER,
                        useValue() {
                            inject(MIconService).registerIcon(...icons);
                        },
                        multi: true
                    }
                ]
            };
        }
        return MIconModule.moduleIconsRegistry(icons);
    }
}
MIconModule.moduleIconsRegistry = (iconsConfig) => ({
    ngModule: MIconModule,
    providers: [
        {
            provide: MIconService,
            useClass: MIconFolderService
        },
        {
            provide: M_ICONS_REGISTRY_PATH,
            useValue: 
            // When is in dev mode, do not get icons from assets
            iconsConfig.prodMode
                ? 'assets/mercadona/icons/'
                : `https://storage.googleapis.com/angular-public-bucket/fwk-angular-icons/${iconsConfig.registry === 'file' ? '' : 'icons/'}`
        }
    ]
});
MIconModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MIconModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.9", ngImport: i0, type: MIconModule, declarations: [MIconComponent], exports: [MIconComponent] });
MIconModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MIconModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [MIconComponent],
                    exports: [MIconComponent]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { MIconComponent, MIconModule, MIconService };
